/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 9: Figure Footnote Text + Keep-With-Next on figures that have footnotes
// Two-phase: detect first, then apply with clean proxy state
//
// CACHE: Every paragraph confirmed as a figure footnote (whether it needed
// restyling or was already correct) is registered in structuralParaCache
// under 'figureFootnote'. Run 6 reads this to skip those paras instead of
// re-running text-pattern detection. The 'figureFootnote' source is marked
// active even when no figures are present, so Run 6 can distinguish
// "ran-and-found-nothing" from "didn't run".
// ═══════════════════════════════════════════════════════════════════════════

import { reportResult, paraDetail, applydefaultparagraphformat, RunDetail } from '../runs/common';
import { detectFootnote } from '../runs/footnoteDetection';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';
import { claimParaIds } from './structuralParaCache';

function isFigureCaption(text: string): boolean {
  if (!text?.trim()) return false;
  return /^Figure\s+\d+(?:\.\d+)*/i.test(
    text.trim().replace(/[\u200B-\u200D\uFEFF]/g, "")
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1 detection: footnote scan below a single figure
// ─────────────────────────────────────────────────────────────────────────

/** Hard-stop conditions for the footnote scan. Returns a human-readable
 *  reason string when scanning should halt at this paragraph, else null. */
function footnoteStopReason(pText: string, pStyle: string): string | null {
  const lowStyle = pStyle.toLowerCase();
  if (lowStyle.includes("heading")) return "heading";
  if (lowStyle.includes("caption")) return "caption";
  if (isFigureCaption(pText)) return "new figure caption";
  if (/^Table\s+\d+/i.test(pText.trim())) return "table caption";
  return null;
}

interface FootnoteScanAcc {
  indices: number[];
  claimedIds: string[];
  footnotesFound: number;
  firstFootnoteIdx: number;
}

/** Record a paragraph that detectFootnote confirmed as a footnote.
 *  Claims its id in every case (Run 6 must skip it either way) and only
 *  queues it for restyling when its current style differs from target. */
function recordDetectedFootnote(
  idx: number,
  pId: string,
  pStyle: string,
  footnoteStyle: string,
  acc: FootnoteScanAcc
): void {
  if (acc.firstFootnoteIdx === -1) acc.firstFootnoteIdx = idx;
  acc.footnotesFound++;
  if (pId) acc.claimedIds.push(pId);
  if (pStyle !== footnoteStyle) {
    acc.indices.push(idx);
    console.log(`    → Queued for styling (index ${idx})`);
  }
}

/** Scan paragraphs below a figure for footnote text, collecting indices.
 *
 *  Performance note: this used to call `para.load("text, style")` and
 *  `await context.sync()` on every iteration. Phase 1 already loads
 *  text+style+uniqueLocalId for every body paragraph in a single batch
 *  sync at startup, so the per-paragraph load was redundant and forced
 *  an unnecessary round-trip per scanned paragraph. We rely on the
 *  pre-loaded properties here. detectFootnote() may still trigger its
 *  own internal sync; that's the only round-trip per iteration now.
 *
 *  Cache integration: returns `claimedIds` — the uniqueLocalIds of ALL
 *  paragraphs confirmed as figure footnotes (whether they need restyling
 *  or are already correct). Run 6 (body text) reads the cache and skips
 *  these paras so it doesn't re-run text-pattern detection on them.
 *  Already-correct paras MUST be claimed too — otherwise Run 6 would
 *  restyle them to Body Text.
 */
async function scanForFootnotesBelow(
  paragraphs: Word.ParagraphCollection,
  scanStart: number,
  paraCount: number,
  context: Word.RequestContext,
  footnoteStyle: string
): Promise<{ indices: number[]; footnotesFound: number; firstFootnoteIdx: number; claimedIds: string[] }> {
  const acc: FootnoteScanAcc = { indices: [], claimedIds: [], footnotesFound: 0, firstFootnoteIdx: -1 };
  let consecutiveEmpty = 0;

  for (let idx = scanStart; idx < Math.min(scanStart + 10, paraCount); idx++) {
    const para = paragraphs.items[idx];
    const pText = para.text || "";
    const pStyle = para.style || "";
    const pId = para.uniqueLocalId || "";

    // Stop conditions
    const stop = footnoteStopReason(pText, pStyle);
    if (stop) { console.log(`    Stop (${stop})`); break; }

    if (!pText.trim()) {
      consecutiveEmpty++;
      if (consecutiveEmpty >= 2) { console.log(`    Stop (2 empty paras)`); break; }
      continue;
    }
    consecutiveEmpty = 0;

    const check = await detectFootnote(para, pText, context);
    console.log(`  [${idx - scanStart + 1}] "${pText.trim().substring(0, 50)}" → ${check.detected ? check.reason : "not footnote"}`);

    if (!check.detected) { console.log(`    Stop (not footnote)`); break; }

    recordDetectedFootnote(idx, pId, pStyle, footnoteStyle, acc);
  }

  return {
    indices: acc.indices,
    footnotesFound: acc.footnotesFound,
    firstFootnoteIdx: acc.firstFootnoteIdx,
    claimedIds: acc.claimedIds,
  };
}

/**
 * Apply "Keep with next" paragraph formatting via OOXML surgery on the
 * paragraph's own <w:pPr> block — strictly scoped to the active <w:p>
 * element.
 *
 * BUG FIXED (false-positive "already set" round 2):
 *
 *   range.getOoxml() does NOT return only the paragraph's XML — it
 *   returns a self-contained WordprocessingML package fragment that
 *   includes /word/styles.xml. Style definitions in styles.xml have
 *   their own <w:pPr> blocks, and Word's built-in caption/heading
 *   styles (Caption, Caption Figure, Heading 1..9, etc.) include
 *   <w:keepNext/> in those pPr blocks. Our previous regex
 *   `enabledRe.test(working)` matched <w:keepNext/> inside a style
 *   definition and wrongly returned alreadySet:true — the paragraph's
 *   own pPr never had keepNext applied.
 *
 *   Symptom: every test run reported "Keep with next already set on
 *   active pPr" even though the diagnostic dump clearly showed the
 *   active pPr did NOT contain keepNext. The KWN the user saw
 *   visually on the caption came from the Caption Figure style's
 *   built-in keepNext (style inheritance), not from our code.
 *
 *   Fix: extract ONLY the first <w:p>...</w:p> block from the OOXML
 *   and run all detection + injection against that fragment. Style
 *   definitions live outside <w:p> (inside <w:style> wrappers in
 *   styles.xml), so they no longer interfere.
 *
 *   We also re-mask <w:pPrChange> within the <w:p> block, since
 *   track-changes revisions still embed their previous pPr state
 *   there. <w:p> doesn't nest itself, so finding the first </w:p> is
 *   unambiguous.
 */
async function applyKeepWithNext(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<{ applied: boolean; alreadySet: boolean }> {
  try {
    const range = para.getRange(Word.RangeLocation.whole);
    const ooxmlResult = range.getOoxml();
    await context.sync();
    const original = ooxmlResult.value;

    // 1) Locate the first <w:p ...> opening tag.
    const pStartRe = /<w:p\b[^>]*>/;
    const pStartMatch = pStartRe.exec(original);
    const pStart = pStartMatch?.index;
    if (pStart === undefined) {
      console.log(`  ⚠ No <w:p> in OOXML — skipping`);
      return { applied: false, alreadySet: false };
    }

    // 2) Find the matching </w:p>.
    const pCloseIdx = original.indexOf('</w:p>', pStart);
    if (pCloseIdx === -1) {
      console.log(`  ⚠ No </w:p> in OOXML — skipping`);
      return { applied: false, alreadySet: false };
    }
    const pEnd = pCloseIdx + '</w:p>'.length;

    const before = original.substring(0, pStart);
    let pBlock  = original.substring(pStart, pEnd);
    const after  = original.substring(pEnd);

    // 3) Mask <w:pPrChange> WITHIN the paragraph block.
    const PPRCHANGE_TOKEN = '___PPRCHANGE_BLOCK___';
    const pprChangeBlocks: string[] = [];
    pBlock = pBlock.replace(
      /<w:pPrChange\b[^>]*>[\s\S]*?<\/w:pPrChange>/g,
      (block) => {
        pprChangeBlocks.push(block);
        return PPRCHANGE_TOKEN;
      }
    );

    // 4) Detect keepNext on the active pPr.
    const disabledRe = /<w:keepNext\s+w:val=["'](?:0|false)["']\s*\/?>/g;
    const enabledRe  = /<w:keepNext(?:\s+w:val=["'](?:1|true)["'])?\s*\/>/;

    const hasDisabled = disabledRe.test(pBlock);
    const hasEnabled  = enabledRe.test(pBlock);

    if (!hasDisabled && hasEnabled) {
      return { applied: false, alreadySet: true };
    }

    // 5) Strip explicit-disable form if present.
    if (hasDisabled) {
      pBlock = pBlock.replace(
        /<w:keepNext\s+w:val=["'](?:0|false)["']\s*\/?>/g,
        ''
      );
    }

    // 6) Inject <w:keepNext/>.
    if (/<w:pPr\b[^>]*>/.test(pBlock)) {
      pBlock = pBlock.replace(/(<w:pPr\b[^>]*>)/, '$1<w:keepNext/>');
    } else {
      pBlock = pBlock.replace(/(<w:p\b[^>]*>)/, '$1<w:pPr><w:keepNext/></w:pPr>');
    }

    // 7) Restore pPrChange blocks.
    let pprIdx = 0;
    pBlock = pBlock.replace(new RegExp(PPRCHANGE_TOKEN, 'g'), () => pprChangeBlocks[pprIdx++]);

    // 8) Reassemble and write back with track changes off.
    const updated = before + pBlock + after;
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();
    range.insertOoxml(updated, Word.InsertLocation.replace);
    await context.sync();
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();

    return { applied: true, alreadySet: false };
  } catch (e) {
    console.log(`  ⚠ Could not apply keep-with-next: ${e?.message ? e.message : e}`);
    return { applied: false, alreadySet: false };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1 setup + detection orchestration
// ─────────────────────────────────────────────────────────────────────────

interface Phase1Setup {
  paragraphs: Word.ParagraphCollection;
  inlinePictures: Word.InlinePictureCollection;
  paraCount: number;
  totalFigures: number;
}

async function loadFiguresAndParagraphs(context: Word.RequestContext): Promise<Phase1Setup> {
  const paragraphs = context.document.body.paragraphs;
  const inlinePictures = context.document.body.inlinePictures;
  paragraphs.load("items/text,items/style,items/uniqueLocalId");
  inlinePictures.load("items/paragraph/uniqueLocalId");
  await context.sync();

  return {
    paragraphs,
    inlinePictures,
    paraCount: paragraphs.items.length,
    totalFigures: inlinePictures.items.length,
  };
}

interface FigureResult {
  indices: number[];
  claimedIds: string[];
  footnotesFound: number;
  /** Body-paragraph index marked for "Keep with next", or -1 if none. */
  kwnTargetIdx: number;
}

interface FigureScanContext {
  paragraphs: Word.ParagraphCollection;
  paraCount: number;
  paraIdToIdx: { [id: string]: number };
  totalFigures: number;
  footnoteStyle: string;
}

async function processOneFigure(
  context: Word.RequestContext,
  picture: Word.InlinePicture,
  fIndex: number,
  scan: FigureScanContext
): Promise<FigureResult> {
  const { paragraphs, paraCount, paraIdToIdx, totalFigures, footnoteStyle } = scan;
  const empty: FigureResult = { indices: [], claimedIds: [], footnotesFound: 0, kwnTargetIdx: -1 };

  console.log(`${"─".repeat(50)}\n[Figure ${fIndex + 1} of ${totalFigures}]`);

  const figureUniqueId = picture.paragraph.uniqueLocalId;
  const figureIdx = figureUniqueId ? (paraIdToIdx[figureUniqueId] ?? -1) : -1;
  if (figureIdx === -1) {
    console.log(`  Could not locate picture's paragraph in body — skipping`);
    return empty;
  }

  let scanStart = figureIdx + 1;
  if (scanStart < paraCount) {
    const nextText = paragraphs.items[scanStart].text || "";
    if (isFigureCaption(nextText)) {
      console.log(`  Caption: "${nextText.trim().substring(0, 50)}"`);
      scanStart++;
    }
  }

  console.log(`  Scanning for footnotes from index ${scanStart}...`);
  const result = await scanForFootnotesBelow(paragraphs, scanStart, paraCount, context, footnoteStyle);

  let kwnTargetIdx = -1;
  if (result.footnotesFound > 0) {
    // ── THE ONE-LINE FIX ──────────────────────────────────────────────
    // scanStart - 1 is always the caption (if one exists) or the figure
    // paragraph itself — the last guaranteed non-empty paragraph with
    // content above the footnote block, regardless of any empty lines
    // that may sit between it and the footnotes.
    kwnTargetIdx = scanStart - 1;
    // ─────────────────────────────────────────────────────────────────
    const targetText = (paragraphs.items[kwnTargetIdx]?.text || "").trim();
    const targetPreview = targetText.length > 0
      ? `"${targetText.substring(0, 60)}"`
      : "(empty paragraph — likely picture-only)";
    console.log(`  → Body idx ${kwnTargetIdx} marked for "Keep with next" (figure/caption above footnotes)`);
    console.log(`     target text: ${targetPreview}`);
  }

  return { indices: result.indices, claimedIds: result.claimedIds, footnotesFound: result.footnotesFound, kwnTargetIdx };
}

interface DetectionResult {
  indicesToStyle: number[];
  allClaimedIds: string[];
  paragraphIndicesForKeepWithNext: Set<number>;
  footnotesFound: number;
}

async function detectAllFigureFootnotes(
  context: Word.RequestContext,
  setup: Phase1Setup,
  footnoteStyle: string
): Promise<DetectionResult> {
  const { paragraphs, inlinePictures, paraCount, totalFigures } = setup;

  const indicesToStyle: number[] = [];
  const allClaimedIds: string[] = [];
  const paragraphIndicesForKeepWithNext = new Set<number>();
  let footnotesFound = 0;

  const paraIdToIdx: { [id: string]: number } = {};
  for (let i = 0; i < paraCount; i++) {
    const id = paragraphs.items[i].uniqueLocalId;
    if (id) paraIdToIdx[id] = i;
  }

  const scan: FigureScanContext = { paragraphs, paraCount, paraIdToIdx, totalFigures, footnoteStyle };

  for (let f = 0; f < inlinePictures.items.length; f++) {
    const r = await processOneFigure(context, inlinePictures.items[f], f, scan);
    footnotesFound += r.footnotesFound;
    indicesToStyle.push(...r.indices);
    allClaimedIds.push(...r.claimedIds);
    if (r.kwnTargetIdx >= 0) paragraphIndicesForKeepWithNext.add(r.kwnTargetIdx);
  }

  return { indicesToStyle, allClaimedIds, paragraphIndicesForKeepWithNext, footnotesFound };
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 2a: apply footnote styles with clean proxy state
// ─────────────────────────────────────────────────────────────────────────

async function applyFootnoteStyles(
  context: Word.RequestContext,
  indicesToStyle: number[],
  footnoteStyle: string,
  changedParas: RunDetail[]
): Promise<number> {
  if (indicesToStyle.length === 0) return 0;

  console.log(`\n── Phase 2a: Applying ${indicesToStyle.length} footnote style change(s) ──\n`);

  const freshParas = context.document.body.paragraphs;
  freshParas.load("items");
  await context.sync();

  for (const fnIdx of indicesToStyle) {
    if (fnIdx >= freshParas.items.length) continue;
    freshParas.items[fnIdx].load("text, uniqueLocalId");
  }
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();

  const snapshots: Array<{ fnIdx: number; txt: string; id: string }> = [];
  for (const fnIdx of indicesToStyle) {
    if (fnIdx >= freshParas.items.length) continue;
    snapshots.push({
      fnIdx,
      txt: (freshParas.items[fnIdx].text || "").trim(),
      id: freshParas.items[fnIdx].uniqueLocalId || "",
    });
  }
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  for (const { fnIdx } of snapshots) {
    freshParas.items[fnIdx].style = footnoteStyle;
  }
  await context.sync();

  for (const { fnIdx } of snapshots) {
    if (fnIdx >= freshParas.items.length) continue;
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await applydefaultparagraphformat(freshParas.items[fnIdx], footnoteStyle, context);
  }
  await context.sync();

  let footnotesStyled = 0;
  for (const { fnIdx, txt, id } of snapshots) {
    footnotesStyled++;
    changedParas.push(paraDetail(id, txt, "→ " + footnoteStyle));
    console.log(`  [Index ${fnIdx}] ✓ Styled "${footnoteStyle}"`);
  }
  return footnotesStyled;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 2b: apply "Keep with next" to anchor paragraphs + drop comments
// ─────────────────────────────────────────────────────────────────────────

async function applyKwnToTargets(
  context: Word.RequestContext,
  kwnParas: Word.ParagraphCollection,
  targetIndices: number[],
  preInfo: { [idx: number]: { txt: string; style: string } }
): Promise<{ appliedIndices: number[]; keepWithNextApplied: number; keepWithNextAlreadySet: number }> {
  const appliedIndices: number[] = [];
  let keepWithNextApplied = 0;
  let keepWithNextAlreadySet = 0;

  for (const targetIdx of targetIndices) {
    if (targetIdx < 0 || targetIdx >= kwnParas.items.length) continue;
    const info = preInfo[targetIdx];
    console.log(`  [Body idx ${targetIdx}] Targeting paragraph (figure/caption above footnotes):`);
    console.log(`     text: "${info.txt.substring(0, 80)}"`);
    console.log(`     style: "${info.style}"`);

    const res = await applyKeepWithNext(kwnParas.items[targetIdx], context);

    if (res.applied) {
      keepWithNextApplied++;
      appliedIndices.push(targetIdx);
      console.log(`  [Body idx ${targetIdx}] ✓ Keep with next applied (track changes off for this write — image not flagged as a revision)`);
    } else if (res.alreadySet) {
      keepWithNextAlreadySet++;
      console.log(`  [Body idx ${targetIdx}] ✓ Keep with next already directly set on this paragraph`);
    } else {
      console.log(`  [Body idx ${targetIdx}] ⚠ Could not apply keep with next`);
    }
  }

  return { appliedIndices, keepWithNextApplied, keepWithNextAlreadySet };
}

async function dropKwnComments(
  context: Word.RequestContext,
  appliedIndices: number[],
  preInfo: { [idx: number]: { txt: string; style: string } },
  changedParas: RunDetail[]
): Promise<void> {
  const refreshed = context.document.body.paragraphs;
  refreshed.load("items");
  await context.sync();
  for (const targetIdx of appliedIndices) {
    if (targetIdx < refreshed.items.length) {
      refreshed.items[targetIdx].load("text, uniqueLocalId");
    }
  }
  await context.sync();

  const KWN_COMMENT = 'Keep with next applied';

  const post: Array<{ targetIdx: number; txt: string; id: string }> = [];
  for (const targetIdx of appliedIndices) {
    const pre = preInfo[targetIdx] || { txt: "", style: "" };
    let postTxt = pre.txt;
    let postId  = "";
    if (targetIdx < refreshed.items.length) {
      postId  = refreshed.items[targetIdx].uniqueLocalId || "";
      postTxt = (refreshed.items[targetIdx].text || "").trim() || pre.txt;
    }
    post.push({ targetIdx, txt: postTxt, id: postId });

    if (targetIdx < refreshed.items.length) {
      try {
        const range = refreshed.items[targetIdx].getRange(Word.RangeLocation.content);
        range.insertComment(KWN_COMMENT);
      } catch (e) {
        console.log(`  [Body idx ${targetIdx}] ⚠ Could not insert KWN comment: ${(e as Error)?.message || e}`);
      }
    }
  }
  await context.sync();

  for (const { targetIdx, txt, id } of post) {
    const label = txt.length > 0 ? txt : "(anchor paragraph)";
    changedParas.push(paraDetail(id, label, "Keep with next applied + comment added"));
    console.log(`  [Body idx ${targetIdx}] ✓ Comment dropped (Go To id="${id}")`);
  }
}

async function applyKeepWithNextPhase(
  context: Word.RequestContext,
  paragraphIndicesForKeepWithNext: Set<number>,
  changedParas: RunDetail[]
): Promise<{ keepWithNextApplied: number; keepWithNextAlreadySet: number }> {
  if (paragraphIndicesForKeepWithNext.size === 0) {
    return { keepWithNextApplied: 0, keepWithNextAlreadySet: 0 };
  }

  console.log(`\n── Phase 2b: Applying "Keep with next" to ${paragraphIndicesForKeepWithNext.size} anchor paragraph(s) ──\n`);

  const targetIndices = Array.from(paragraphIndicesForKeepWithNext);

  const kwnParas = context.document.body.paragraphs;
  kwnParas.load("items");
  await context.sync();

  for (const targetIdx of targetIndices) {
    if (targetIdx >= 0 && targetIdx < kwnParas.items.length) {
      kwnParas.items[targetIdx].load("text, style");
    }
  }
  await context.sync();

  const preInfo: { [idx: number]: { txt: string; style: string } } = {};
  for (const targetIdx of targetIndices) {
    if (targetIdx >= 0 && targetIdx < kwnParas.items.length) {
      preInfo[targetIdx] = {
        txt: (kwnParas.items[targetIdx].text || "").trim(),
        style: kwnParas.items[targetIdx].style || "",
      };
    }
  }

  const applied = await applyKwnToTargets(context, kwnParas, targetIndices, preInfo);

  if (applied.appliedIndices.length > 0) {
    await dropKwnComments(context, applied.appliedIndices, preInfo, changedParas);
  }

  return {
    keepWithNextApplied: applied.keepWithNextApplied,
    keepWithNextAlreadySet: applied.keepWithNextAlreadySet,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Reporting
// ─────────────────────────────────────────────────────────────────────────

interface Run9Stats {
  totalFigures: number;
  footnotesFound: number;
  footnotesStyled: number;
  keepWithNextApplied: number;
  keepWithNextAlreadySet: number;
  allClaimedCount: number;
}

function reportRun9Results(stats: Run9Stats, footnoteStyle: string, changedParas: RunDetail[]): void {
  console.log("=".repeat(70));
  console.log(`Figures: ${stats.totalFigures} | Footnotes found: ${stats.footnotesFound} | Footnotes styled: ${stats.footnotesStyled}`);
  console.log(`Keep with next: ${stats.keepWithNextApplied} applied, ${stats.keepWithNextAlreadySet} already set`);
  console.log(`Cache: registered ${stats.allClaimedCount} figure-footnote para ID(s)`);
  console.log("✓ Figure Footnote check complete!");

  const anyChange = stats.footnotesStyled > 0 || stats.keepWithNextApplied > 0;
  if (anyChange) {
    reportResult('Figure Footnote Styling Complete', [
      { label: `Footnotes styled with ${footnoteStyle} style`,            count: stats.footnotesStyled },
      { label: 'Anchor paragraphs given "Keep with next"',                 count: stats.keepWithNextApplied },
      { label: 'Anchor paragraphs already had "Keep with next" directly', count: stats.keepWithNextAlreadySet },
      ...changedParas,
    ]);
  } else {
    reportResult('Figure Footnote — no changes needed', [
      { label: 'No footnote text found below figures',                    note: 'No changes made' },
      { label: 'Anchor paragraphs already had "Keep with next" directly', count: stats.keepWithNextAlreadySet },
    ]);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────

export async function run9() {
  await Word.run(async (context) => {
    try {
      const cfg = getConfig();
      const footnoteStyle = cfg.run9_figureFootnotes.footnoteStyle;

      await ensureRunStyles(context, 9);

      const changedParas: RunDetail[] = [];

      console.log("=".repeat(70));
      console.log(`=== FIGURE FOOTNOTE TEXT CHECKER (${cfg.clientName}) ===`);
      console.log("=".repeat(70));

      // ── PHASE 1: load figures + paragraphs ──
      const setup = await loadFiguresAndParagraphs(context);

      if (setup.totalFigures === 0) {
        claimParaIds([], 'figureFootnote');
        reportResult('Figure Footnote — no figures found', [{ label: 'No figures in document', note: 'Nothing to process' }]);
        return;
      }

      // ── PHASE 1: detect footnotes below each figure ──
      const detection = await detectAllFigureFootnotes(context, setup, footnoteStyle);

      // ── PHASE 2a: apply footnote styles ──
      const footnotesStyled = await applyFootnoteStyles(context, detection.indicesToStyle, footnoteStyle, changedParas);

      // ── PHASE 2b: apply "Keep with next" to anchor paragraphs ──
      const kwn = await applyKeepWithNextPhase(context, detection.paragraphIndicesForKeepWithNext, changedParas);

      claimParaIds(detection.allClaimedIds, 'figureFootnote');

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      reportRun9Results(
        {
          totalFigures: setup.totalFigures,
          footnotesFound: detection.footnotesFound,
          footnotesStyled,
          keepWithNextApplied: kwn.keepWithNextApplied,
          keepWithNextAlreadySet: kwn.keepWithNextAlreadySet,
          allClaimedCount: detection.allClaimedIds.length,
        },
        footnoteStyle,
        changedParas
      );
    } catch (error) {
      console.error("Error in run9:", error);
      console.error("Message:", error.message);
    }
  });
}